import { Box, Button, Checkbox, Icon, Input, Modal, Popover, SpaceBetween } from '@cloudscape-design/components';
import React from 'react';
import { fetchItemsByIds } from '../api/generic';
import { useAuthContext } from '../contexts/AuthContext';
import InventoryItem, { RecipeIngredient } from '../models/Inventory';
import { prefixCollection } from '../utils/prefix';
import Flex from './Flex';

export interface ConfirmIngredientsModalProps {
  showIngredientsModal: boolean;
  setShowIngredientsModal: (visible: boolean) => void;
  editedIngredients: {
    item: InventoryItem;
    quantity: number;
  }[];
  setEditedIngredients: React.Dispatch<React.SetStateAction<RecipeIngredient[]>>;
  confirmIngredients: (subtract: boolean) => void;
}

export const ConfirmIngredientsModal: React.FC<ConfirmIngredientsModalProps> = ({
  showIngredientsModal,
  setShowIngredientsModal,
  editedIngredients,
  setEditedIngredients,
  confirmIngredients,
}) => {
  const { tenant } = useAuthContext();
  const [subtractFromInventory, setSubtractFromInventory] = React.useState(true);
  const [inventoryIngredients, setInventoryIngredients] = React.useState<InventoryItem[]>([]);

  React.useEffect(() => {
    const fetchInventoryItems = async () => {
      const items = await fetchItemsByIds<InventoryItem>(
        prefixCollection('inventory', tenant),
        editedIngredients.map((ing) => ing.item.id),
      );
      setInventoryIngredients(items);
    };

    fetchInventoryItems();
  }, [tenant, editedIngredients]);

  // 🔎 Check if any item would go negative
  const insufficientStock = editedIngredients.some((ingredient) => {
    const dbItem = inventoryIngredients.find((item) => item.id === ingredient.item.id);
    return dbItem ? dbItem.quantity < ingredient.quantity : true; // treat missing as insufficient
  });

  return (
    <Modal
      visible={showIngredientsModal}
      onDismiss={() => setShowIngredientsModal(false)}
      header="Confirm Ingredients"
      footer={
        <div style={{ float: 'right' }}>
          <SpaceBetween size="m" direction="horizontal">
            <Button onClick={() => setShowIngredientsModal(false)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={subtractFromInventory && insufficientStock} // ✅ Disable if not enough
              onClick={() => confirmIngredients(subtractFromInventory)}
            >
              Confirm
            </Button>
          </SpaceBetween>
        </div>
      }
    >
      <SpaceBetween size="l" direction="vertical">
        {editedIngredients.map((ingredient, idx) => {
          const dbItem = inventoryIngredients.find((item) => item.id === ingredient.item.id);

          return (
            <Flex key={idx} justify="start" align="center">
              {ingredient.item.name} ({dbItem?.quantity ?? 0} {ingredient.item.unit}){' '}
              {dbItem && dbItem.quantity >= ingredient.quantity ? (
                <Icon name="arrow-right" />
              ) : (
                <Popover
                  dismissButton={false}
                  position="top"
                  size="small"
                  triggerType="custom"
                  content={
                    <span>
                      Not enough items in inventory.
                      <br />
                      Available: {dbItem?.quantity ?? 0}, Needed: {ingredient.quantity}
                    </span>
                  }
                >
                  <span style={{ color: 'red', cursor: 'help' }}>
                    <Icon name="status-warning" />
                  </span>
                </Popover>
              )}
              <div style={{ width: 96 }}>
                <Input
                  value={ingredient.quantity.toString()}
                  type="number"
                  onChange={({ detail }) => {
                    const newQuantity = parseFloat(detail.value);
                    setEditedIngredients((prev) => {
                      const copy = [...prev];
                      copy[idx].quantity = isNaN(newQuantity) ? 0 : newQuantity;
                      return copy;
                    });
                  }}
                />
              </div>
              {ingredient.item.unit}
            </Flex>
          );
        })}

        <Flex justify="start" align="center">
          <Checkbox
            controlId="subtract-checkbox"
            checked={subtractFromInventory}
            onChange={({ detail }) => {
              setSubtractFromInventory(detail.checked);
            }}
          >
            <Box variant="awsui-key-label">Subtract used ingredients from inventory</Box>
          </Checkbox>
        </Flex>
      </SpaceBetween>
    </Modal>
  );
};
